import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Typography,
  Alert,
  Stack,
} from '@mui/material'
import { Add, Edit, Delete, Visibility, Warning, Timer, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, ExportButtons } from '@components/common'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const LOG_TYPES = [
  { value: 'daily', label: 'Daily Log' },
  { value: 'treatment', label: 'Treatment Log' },
  { value: 'error', label: 'Error Log' },
  { value: 'maintenance', label: 'Maintenance Log' },
]

const EquipmentLogsList = () => {
  const { canCreate, canEdit, canDelete } = usePermissions()
  const [logs, setLogs] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)
  const [saving, setSaving] = useState(false)
  const [downtimeSummary, setDowntimeSummary] = useState([])
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    log_type: '',
    asset: '',
  })

  const [formData, setFormData] = useState({
    asset: '',
    log_type: 'daily',
    log_date: '',
    log_time: '',
    beam_on_time_minutes: '',
    machine_hours: '',
    patients_treated: '',
    error_code: '',
    error_description: '',
    resolution: '',
    downtime_minutes: '',
    treatment_interrupted: false,
    treatments_affected: '',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.log_type) params.log_type = filters.log_type
      if (filters.asset) params.asset = filters.asset

      const [logsRes, assetsRes, downtimeRes] = await Promise.all([
        api.get('/maintenance/equipment-logs/', { params }),
        api.get('/assets/assets/'),
        api.get('/maintenance/equipment-logs/downtime_summary/'),
      ])
      setLogs(logsRes.data.results || logsRes.data)
      // Filter assets to show only radiation oncology equipment (LINAC, CT Sim, etc.)
      const allAssets = assetsRes.data.results || assetsRes.data
      setAssets(allAssets)
      setDowntimeSummary(downtimeRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load equipment logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  // Export columns definition
  const exportColumns = useMemo(() => [
    { field: 'log_date', headerName: 'Date', renderValue: (v) => formatDate(v) || '-' },
    { field: 'log_time', headerName: 'Time' },
    { field: 'asset_name', headerName: 'Equipment' },
    { field: 'log_type', headerName: 'Type', renderValue: (v) => LOG_TYPES.find((t) => t.value === v)?.label || v },
    { field: 'beam_on_time_minutes', headerName: 'Beam Time (min)' },
    { field: 'patients_treated', headerName: 'Patients' },
    { field: 'downtime_minutes', headerName: 'Downtime (min)' },
    { field: 'treatment_interrupted', headerName: 'Interrupted', renderValue: (v) => v ? 'Yes' : 'No' },
    { field: 'error_code', headerName: 'Error Code' },
  ], [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ log_type: '', asset: '' })
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  const handleAdd = () => {
    setSelectedLog(null)
    const today = new Date().toISOString().split('T')[0]
    const now = new Date().toTimeString().slice(0, 5)
    setFormData({
      asset: '',
      log_type: 'daily',
      log_date: today,
      log_time: now,
      beam_on_time_minutes: '',
      machine_hours: '',
      patients_treated: '',
      error_code: '',
      error_description: '',
      resolution: '',
      downtime_minutes: '',
      treatment_interrupted: false,
      treatments_affected: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (log) => {
    setSelectedLog(log)
    setFormData({
      asset: log.asset || '',
      log_type: log.log_type || 'daily',
      log_date: log.log_date || '',
      log_time: log.log_time || '',
      beam_on_time_minutes: log.beam_on_time_minutes || '',
      machine_hours: log.machine_hours || '',
      patients_treated: log.patients_treated || '',
      error_code: log.error_code || '',
      error_description: log.error_description || '',
      resolution: log.resolution || '',
      downtime_minutes: log.downtime_minutes || '',
      treatment_interrupted: log.treatment_interrupted || false,
      treatments_affected: log.treatments_affected || '',
      notes: log.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = (log) => {
    setSelectedLog(log)
    setDetailOpen(true)
  }

  const handleSave = async () => {
    if (!formData.asset || !formData.log_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      const payload = {
        ...formData,
        beam_on_time_minutes: formData.beam_on_time_minutes || null,
        machine_hours: formData.machine_hours || null,
        patients_treated: formData.patients_treated || null,
        downtime_minutes: formData.downtime_minutes || null,
        treatments_affected: formData.treatments_affected || null,
        log_time: formData.log_time || null,
      }
      if (selectedLog) {
        await api.patch(`/maintenance/equipment-logs/${selectedLog.id}/`, payload)
        toast.success('Equipment log updated')
      } else {
        await api.post('/maintenance/equipment-logs/', payload)
        toast.success('Equipment log created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving log:', error)
      toast.error(error.response?.data?.error || 'Failed to save equipment log')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (log) => {
    if (!window.confirm(`Delete this equipment log?`)) return
    try {
      await api.delete(`/maintenance/equipment-logs/${log.id}/`)
      toast.success('Equipment log deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting log:', error)
      toast.error('Failed to delete equipment log')
    }
  }

  const getLogTypeColor = (type) => {
    const colors = {
      daily: 'primary',
      treatment: 'success',
      error: 'error',
      maintenance: 'warning',
    }
    return colors[type] || 'default'
  }

  const columns = [
    {
      field: 'log_date',
      headerName: 'Date',
      renderCell: (v) => formatDate(v) || '-',
      sortable: true,
    },
    { field: 'log_time', headerName: 'Time', renderCell: (v) => v || '-' },
    { field: 'asset_name', headerName: 'Equipment', sortable: true },
    {
      field: 'log_type',
      headerName: 'Type',
      renderCell: (v) => (
        <Chip
          label={LOG_TYPES.find((t) => t.value === v)?.label || v}
          size="small"
          color={getLogTypeColor(v)}
        />
      ),
    },
    {
      field: 'beam_on_time_minutes',
      headerName: 'Beam Time',
      renderCell: (v) => v ? `${v} min` : '-',
    },
    {
      field: 'patients_treated',
      headerName: 'Patients',
      renderCell: (v) => v || '-',
    },
    {
      field: 'downtime_minutes',
      headerName: 'Downtime',
      renderCell: (v) => v ? (
        <Chip label={`${v} min`} size="small" color="error" icon={<Timer />} />
      ) : '-',
    },
    {
      field: 'treatment_interrupted',
      headerName: 'Interrupted',
      renderCell: (v) => v ? (
        <Chip label="Yes" size="small" color="error" />
      ) : '-',
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          {canEdit('equipment_logs') && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {canDelete('equipment_logs') && (
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  // Calculate stats
  const totalDowntime = logs.reduce((sum, l) => sum + (l.downtime_minutes || 0), 0)
  const treatmentInterruptions = logs.filter((l) => l.treatment_interrupted).length
  const totalPatients = logs.reduce((sum, l) => sum + (l.patients_treated || 0), 0)

  return (
    <Box>
      <PageHeader
        title="Equipment Logs"
        subtitle="LINAC and radiation equipment operational logs"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Oncology' },
          { label: 'Equipment Logs' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <ExportButtons
              data={logs}
              columns={exportColumns}
              title="Equipment Logs"
              filename="equipment_logs"
              filters={{
                Type: filters.log_type ? LOG_TYPES.find(t => t.value === filters.log_type)?.label : null,
                Equipment: filters.asset ? assets.find(a => a.id === parseInt(filters.asset))?.name : null,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={activeFilterCount > 0 ? 'primary' : 'inherit'}
            >
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
            {canCreate('equipment_logs') && (
              <Button startIcon={<Add />} onClick={handleAdd}>
                Add Log Entry
              </Button>
            )}
          </Stack>
        }
      />

      {/* Filters Panel */}
      {showFilters && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Log Type</InputLabel>
                <Select
                  value={filters.log_type}
                  onChange={(e) => handleFilterChange('log_type', e.target.value)}
                  label="Log Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {LOG_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Equipment</InputLabel>
                <Select
                  value={filters.asset}
                  onChange={(e) => handleFilterChange('asset', e.target.value)}
                  label="Equipment"
                >
                  <MenuItem value="">All</MenuItem>
                  {assets.map((a) => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="primary">
                {totalPatients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Patients Treated
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="error.main">
                {totalDowntime} min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Downtime
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {treatmentInterruptions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Treatment Interruptions
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {treatmentInterruptions > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          {treatmentInterruptions} treatment interruption(s) recorded
        </Alert>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          searchPlaceholder="Search equipment logs..."
          emptyMessage="No equipment logs found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedLog ? 'Edit Equipment Log' : 'Add Equipment Log'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedLog ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Equipment</InputLabel>
              <Select
                value={formData.asset}
                onChange={(e) => setFormData({ ...formData, asset: e.target.value })}
                label="Equipment"
              >
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Log Type</InputLabel>
              <Select
                value={formData.log_type}
                onChange={(e) => setFormData({ ...formData, log_type: e.target.value })}
                label="Log Type"
              >
                {LOG_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Date"
              type="date"
              value={formData.log_date}
              onChange={(e) => setFormData({ ...formData, log_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Time"
              type="time"
              value={formData.log_time}
              onChange={(e) => setFormData({ ...formData, log_time: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Beam-On Time (minutes)"
              type="number"
              value={formData.beam_on_time_minutes}
              onChange={(e) => setFormData({ ...formData, beam_on_time_minutes: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Machine Hours"
              type="number"
              value={formData.machine_hours}
              onChange={(e) => setFormData({ ...formData, machine_hours: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Patients Treated"
              type="number"
              value={formData.patients_treated}
              onChange={(e) => setFormData({ ...formData, patients_treated: e.target.value })}
            />
          </Grid>

          {/* Error/Issue Section */}
          {(formData.log_type === 'error' || formData.log_type === 'maintenance') && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Error/Issue Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Error Code"
                  value={formData.error_code}
                  onChange={(e) => setFormData({ ...formData, error_code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Downtime (minutes)"
                  type="number"
                  value={formData.downtime_minutes}
                  onChange={(e) => setFormData({ ...formData, downtime_minutes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Treatments Affected"
                  type="number"
                  value={formData.treatments_affected}
                  onChange={(e) => setFormData({ ...formData, treatments_affected: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Error Description"
                  value={formData.error_description}
                  onChange={(e) => setFormData({ ...formData, error_description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Resolution"
                  value={formData.resolution}
                  onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Treatment Interrupted?</InputLabel>
                  <Select
                    value={formData.treatment_interrupted}
                    onChange={(e) => setFormData({ ...formData, treatment_interrupted: e.target.value })}
                    label="Treatment Interrupted?"
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Equipment Log Details"
        maxWidth="sm"
        actions={
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
        }
      >
        {selectedLog && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Equipment</Box>
                <Box fontWeight={600}>{selectedLog.asset_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Log Type</Box>
                <Chip
                  label={LOG_TYPES.find((t) => t.value === selectedLog.log_type)?.label}
                  size="small"
                  color={getLogTypeColor(selectedLog.log_type)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Date</Box>
                <Box fontWeight={600}>
                  {formatDate(selectedLog.log_date) || '-'}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Time</Box>
                <Box fontWeight={600}>{selectedLog.log_time || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Beam Time</Box>
                <Box fontWeight={600}>{selectedLog.beam_on_time_minutes ? `${selectedLog.beam_on_time_minutes} min` : '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Machine Hours</Box>
                <Box fontWeight={600}>{selectedLog.machine_hours || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Patients</Box>
                <Box fontWeight={600}>{selectedLog.patients_treated || '-'}</Box>
              </Box>
            </Grid>
            {selectedLog.error_code && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Error Code</Box>
                  <Box fontWeight={600}>{selectedLog.error_code}</Box>
                </Box>
              </Grid>
            )}
            {selectedLog.error_description && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Error Description</Box>
                  <Box>{selectedLog.error_description}</Box>
                </Box>
              </Grid>
            )}
            {selectedLog.resolution && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Resolution</Box>
                  <Box>{selectedLog.resolution}</Box>
                </Box>
              </Grid>
            )}
            {selectedLog.downtime_minutes && (
              <Grid item xs={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Downtime</Box>
                  <Chip label={`${selectedLog.downtime_minutes} min`} size="small" color="error" />
                </Box>
              </Grid>
            )}
            {selectedLog.treatment_interrupted && (
              <Grid item xs={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Treatment Interrupted</Box>
                  <Chip label="Yes" size="small" color="error" />
                </Box>
              </Grid>
            )}
            {selectedLog.notes && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Notes</Box>
                  <Box>{selectedLog.notes}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>
    </Box>
  )
}

export default EquipmentLogsList
